import { Pageable } from '../../../mixins/pagination/pagination.mixin';
import { FilteredPublicationsI, PublicationFilterI, PublicationI } from '../../../models/publication';
import { DocumentFilterService } from './document-filter.service';
import { PaginationProvider } from '../../../providers/pagination-provider';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

export class TabPagination {
    public paginationProvider: PaginationProvider<Observable<FilteredPublicationsI>>;
    filter: PublicationFilterI;

    private preSearchPublications: PublicationI[] = [];
    private enginePublications: PublicationI[] = [];
    private preSearchSubscription: Subscription;

    constructor(private documentFilterService: DocumentFilterService) {
        this.paginationProvider = new PaginationProvider<Observable<FilteredPublicationsI>>(
            this.loadPageableData.bind(this)
        );
        this.paginationProvider.setupPagination(100, 1);
        this.paginationProvider.pagination.sort = 'RELEVANCE';
        this.paginationProvider.pagination.size = 12;
    }

    get pagination(): Pageable {
        return this.paginationProvider.pagination;
    }

    get totalItems(): number {
        return this.paginationProvider.totalItems;
    }

    firstPage(filter: PublicationFilterI): Observable<FilteredPublicationsI> {
        this.filter = filter;
        return this.paginationProvider.firstPage();
    }

    nextPage(filter: PublicationFilterI): Observable<FilteredPublicationsI> {
        this.filter = filter;
        return this.paginationProvider.nextPage();
    }

    previousPage(filter: PublicationFilterI): Observable<FilteredPublicationsI> {
        this.filter = filter;
        return this.paginationProvider.previousPage();
    }

    loadPageableData(): Observable<FilteredPublicationsI> {
        const concreteFilter = { ...this.filter };
        this.documentFilterService.baseFilter.pagination = this.pagination;
        concreteFilter.pagination = this.pagination;

        const tabId = this.getTabId(concreteFilter);

        const isFirstPage = this.pagination.page <= 1;
        if (isFirstPage) {
            this.preSearchPublications = [];
            this.enginePublications = [];
            this.loadPreSearchPublications(concreteFilter, tabId);
        }

        return this.documentFilterService.fetchSearchResultsFromServer(concreteFilter).pipe(
            tap(data => {
                if (concreteFilter.attributes.type != null && concreteFilter.attributes.type[0] != null) {
                    this.documentFilterService.hitsOnTab[concreteFilter.attributes.type[0]].next({
                        hits: data.facets,
                        total: data.total
                    });
                }
                this.paginationProvider.updatePagination(data.total);
                this.documentFilterService.facets = data.facets;
                this.documentFilterService.lastPageAddedPublications = data.publicationList.length > 0;

                this.enginePublications = [...this.enginePublications, ...data.publicationList];
                this.publishPublications(tabId);
            })
        );
    }

    private loadPreSearchPublications(concreteFilter: PublicationFilterI, tabId: string): void {
        this.preSearchSubscription?.unsubscribe();
        this.preSearchSubscription = this.documentFilterService
            .fetchPreSearchResultsFromServer(concreteFilter)
            .subscribe({
                next: data => {
                    this.preSearchPublications = data.publicationList ?? [];
                    this.publishPublications(tabId);
                },
                error: () => {
                    this.preSearchPublications = [];
                    this.publishPublications(tabId);
                }
            });
    }

    /**
     * Drop the pre-search results once the user moves past the first page, a document found by the
     * search engine (evidensse) as well going back to its own place in the list.
     */
    hidePreSearchPublications(): void {
        this.preSearchSubscription?.unsubscribe();
        if (this.preSearchPublications.length === 0) {
            return;
        }
        this.preSearchPublications = [];
        const tabId = this.getTabId(this.filter);
        this.publishPublications(tabId);
    }

    private publishPublications(tabId: string): void {
        const preSearchKeys = new Set(this.preSearchPublications.map(publication => this.publicationKey(publication)));
        const deduplicatedEnginePublications = this.enginePublications.filter(
            publication => !preSearchKeys.has(this.publicationKey(publication))
        );

        this.documentFilterService.resultsByTab[tabId].next([
            ...this.preSearchPublications,
            ...deduplicatedEnginePublications
        ]);
    }

    private publicationKey(publication: PublicationI): string {
        return `${publication.reference}@${publication.version}`;
    }

    private getTabId(concreteFilter: PublicationFilterI): string {
        return concreteFilter.attributes.type !== undefined && concreteFilter.attributes.type.length > 0
            ? concreteFilter.attributes.type[0]
            : 'ALL';
    }
}
